import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowUpRight,
  Atom,
  Sparkles,
  Cpu,
  Rocket,
  Bot,
  Zap,
  Plane,
  Telescope,
} from "lucide-react";
import { BUSINESS_AREAS, STATS } from "@/data/content";
import CTASection from "@/components/CTASection";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

const Home = () => {
  useDocumentTitle();
  return (
    <div data-testid="home-page">
      {/* ---------- HERO ---------- */}
      <section className="relative overflow-hidden hero-spotlight">
        <div className="absolute inset-0 bg-grid-lines opacity-60" />
        <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full bg-sky-100/40 blur-3xl -z-0" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 lg:pt-28 pb-24 lg:pb-32">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 border border-slate-200 bg-white/70 backdrop-blur px-3.5 py-1.5 rounded-full text-xs uppercase tracking-[0.22em] text-slate-600 mb-6">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                Innovating Science · Engineering Tomorrow
              </div>

              <h1
                data-testid="hero-headline"
                className="font-display text-4xl sm:text-5xl lg:text-[64px] xl:text-[76px] leading-[1.02] tracking-tight text-[#0B1B3D] font-semibold"
              >
                Engineering the future through{" "}
                <span className="relative inline-block">
                  <span className="text-[#0B1B3D]">Science, Technology</span>
                </span>{" "}
                &{" "}
                <span className="text-amber-600">Artificial Intelligence</span>.
              </h1>

              <p className="mt-6 text-base lg:text-lg text-slate-600 max-w-2xl leading-relaxed">
                Research-driven innovations in advanced motors, intelligent
                software, aerospace technologies, renewable energy and scientific
                solutions — engineered for a smarter, more precise industrial future.
              </p>

              <div className="mt-8 flex flex-wrap items-center gap-3">
                <Link
                  to="/technologies"
                  data-testid="hero-cta-explore"
                  className="inline-flex items-center gap-2 bg-[#0B1B3D] hover:bg-[#1E3A8A] text-white font-medium px-6 py-3.5 rounded-md transition-colors shadow-sm"
                >
                  Explore Technologies
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/contact"
                  data-testid="hero-cta-contact"
                  className="inline-flex items-center gap-2 border border-slate-300 hover:border-[#0B1B3D] text-slate-800 hover:text-[#0B1B3D] font-medium px-6 py-3.5 rounded-md transition-colors"
                >
                  Contact Us
                </Link>
              </div>

              {/* Micro capabilities row */}
              <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-4">
                {[
                  { Icon: Atom, label: "R & D" },
                  { Icon: Cpu, label: "Embedded" },
                  { Icon: Bot, label: "Robotics" },
                  { Icon: Rocket, label: "Aerospace" },
                ].map(({ Icon, label }) => (
                  <div
                    key={label}
                    className="flex items-center gap-2.5 border border-slate-200 rounded-md bg-white/70 backdrop-blur px-3.5 py-2.5"
                  >
                    <Icon className="w-4 h-4 text-sky-500" strokeWidth={1.5} />
                    <span className="text-sm font-medium text-slate-700">{label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right visual */}
            <div className="lg:col-span-5">
              <div className="relative aspect-[4/5] rounded-2xl overflow-hidden border border-slate-200 shadow-xl">
                <img
                  src="https://images.unsplash.com/photo-1644325349124-d1756b79dd42?w=1200&q=80&auto=format&fit=crop"
                  alt="Neural network abstract"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B1B3D]/80 via-[#0B1B3D]/10 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-amber-400 mb-2">
                    Intelligent Systems
                  </div>
                  <div className="font-display text-white text-2xl font-semibold leading-tight">
                    AI, Motion Control & Aerospace, engineered together.
                  </div>
                </div>

                {/* Floating cards */}
                <div className="absolute top-5 right-5 bg-white/95 backdrop-blur border border-white/70 rounded-md px-3 py-2 shadow-lg">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">
                    Efficiency
                  </div>
                  <div className="font-display text-xl text-[#0B1B3D] font-semibold">
                    92%+
                  </div>
                </div>
                <div className="absolute top-24 right-8 bg-white/95 backdrop-blur border border-white/70 rounded-md px-3 py-2 shadow-lg">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">
                    Edge AI
                  </div>
                  <div className="font-display text-xl text-[#0B1B3D] font-semibold">
                    40 TOPS
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="mt-16 lg:mt-20 grid grid-cols-2 lg:grid-cols-4 border-y border-slate-200 divide-x divide-slate-200">
            {STATS.map((s) => (
              <div key={s.v} className="py-6 lg:py-8 px-4 text-center lg:text-left">
                <div className="font-display text-3xl lg:text-4xl font-semibold text-[#0B1B3D]">
                  {s.k}
                </div>
                <div className="mt-1 text-xs uppercase tracking-widest text-slate-500">
                  {s.v}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- ABOUT ---------- */}
      <section className="py-24 lg:py-28 border-b border-slate-200 reveal">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.25em] text-amber-600 font-medium mb-3">
              About Akyvia
            </div>
            <h2 className="font-display text-3xl lg:text-5xl font-semibold text-[#0B1B3D] tracking-tight leading-[1.05]">
              A research-first engineering company.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-6 text-slate-600 leading-relaxed">
            <p>
              Akyvia Sciences and Technologies is a research-driven technology
              company focused on scientific innovation, advanced engineering,
              intelligent automation and future technologies. From the design of
              special electric motors to autonomous aviation and space
              instrumentation, we combine deep science with disciplined
              engineering to build products the world can rely on.
            </p>
            <p>
              Our work spans applied research, product engineering, industrial
              automation, AI systems, aerospace, renewable energy and
              professional training — enabling universities, industries and
              scientific missions to move faster with precision.
            </p>
            <div className="flex flex-wrap gap-2 pt-2">
              {["Innovation", "Precision", "Scientific Rigor", "Reliability"].map(
                (t) => (
                  <span
                    key={t}
                    className="text-xs px-3 py-1.5 rounded-full border border-slate-300 text-slate-700"
                  >
                    {t}
                  </span>
                )
              )}
            </div>
            <div>
              <Link
                to="/about"
                data-testid="about-learn-more-link"
                className="inline-flex items-center gap-2 text-[#0B1B3D] font-medium hover:text-amber-600 transition-colors"
              >
                Learn more about our story
                <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- BUSINESS AREAS (BENTO) ---------- */}
      <section className="py-24 lg:py-28 bg-gradient-to-br from-white to-sky-50/40 border-b border-slate-200 reveal">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
            <div>
              <div className="text-xs uppercase tracking-[0.25em] text-amber-600 font-medium mb-3">
                Capabilities
              </div>
              <h2 className="font-display text-3xl lg:text-5xl font-semibold text-[#0B1B3D] tracking-tight leading-[1.05]">
                Core business areas.
              </h2>
            </div>
            <p className="text-slate-600 max-w-md leading-relaxed">
              Thirteen focused disciplines, one integrated engineering practice —
              from precision motors to autonomous aviation and space sciences.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 auto-rows-[200px] gap-5">
            {BUSINESS_AREAS.map(({ icon: Icon, title, desc, span }, i) => (
              <div
                key={title}
                data-testid={`business-area-${i}`}
                className={`group relative bg-white border border-slate-200 rounded-xl p-6 hover:shadow-xl hover:-translate-y-1 hover:border-sky-300 transition-all duration-300 flex flex-col justify-between overflow-hidden ${span || ""}`}
              >
                <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full bg-sky-100/0 group-hover:bg-sky-100/60 transition-colors duration-500" />
                <div className="relative">
                  <div className="w-11 h-11 rounded-md bg-[#0B1B3D]/5 border border-slate-200 flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5 text-[#0B1B3D]" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-display text-lg lg:text-xl font-semibold text-[#0B1B3D] leading-tight">
                    {title}
                  </h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                    {desc}
                  </p>
                </div>
                <div className="relative mt-4 flex items-center gap-1.5 text-xs uppercase tracking-widest text-amber-600 font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                  Learn more
                  <ArrowRight className="w-3.5 h-3.5" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- RESEARCH & INNOVATION ---------- */}
      <section className="py-24 lg:py-28 border-b border-slate-200 reveal">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <div className="text-xs uppercase tracking-[0.25em] text-amber-600 font-medium mb-3">
              Research & Innovation
            </div>
            <h2 className="font-display text-3xl lg:text-5xl font-semibold text-[#0B1B3D] tracking-tight leading-[1.05]">
              Applied science, engineered into products.
            </h2>
            <p className="mt-6 text-slate-600 leading-relaxed max-w-lg">
              Our research spans applied physics, control theory, machine
              learning and precision mechatronics. We take experimental ideas
              from laboratory prototypes to certified engineering products.
            </p>

            <div className="mt-8 grid grid-cols-2 gap-4">
              {[
                "Applied Research",
                "Scientific Development",
                "Prototype Development",
                "Product Engineering",
                "Experimental Technologies",
                "Technology Commercialization",
              ].map((f) => (
                <div
                  key={f}
                  className="flex items-center gap-2 border border-slate-200 rounded-md px-3 py-2.5 text-sm text-slate-700 bg-white"
                >
                  <Sparkles className="w-3.5 h-3.5 text-sky-500" strokeWidth={1.5} />
                  {f}
                </div>
              ))}
            </div>

            <Link
              to="/research"
              data-testid="research-learn-link"
              className="mt-8 inline-flex items-center gap-2 text-[#0B1B3D] font-medium hover:text-amber-600"
            >
              Explore our research areas
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="lg:col-span-6 order-1 lg:order-2">
            <div className="relative aspect-square rounded-2xl overflow-hidden border border-slate-200 shadow-lg">
              <img
                src="https://images.unsplash.com/photo-1727091506059-69012c069173?w=1400&q=80&auto=format&fit=crop"
                alt="Scientific research laboratory"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-[#0B1B3D]/40 via-transparent to-transparent" />
              <div className="absolute top-4 left-4 bg-white/90 backdrop-blur rounded-md border border-white/70 px-3 py-1.5 text-[10px] uppercase tracking-widest text-slate-600">
                Laboratory
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- MANUFACTURING ---------- */}
      <section className="py-24 lg:py-28 bg-[#0B1B3D] text-white relative overflow-hidden reveal">
        <div className="absolute inset-0 bg-dots opacity-20" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-5">
              <div className="text-xs uppercase tracking-[0.25em] text-amber-400 font-medium mb-3">
                Manufacturing
              </div>
              <h2 className="font-display text-3xl lg:text-5xl font-semibold tracking-tight leading-[1.05]">
                Precision engineering,
                <br />
                built in-house.
              </h2>
              <p className="mt-6 text-slate-300/85 leading-relaxed max-w-md">
                From special-purpose motors and controllers to smart industrial
                devices — our manufacturing is engineered around precision,
                reliability and traceable quality.
              </p>
              <Link
                to="/manufacturing"
                data-testid="manufacturing-explore-link"
                className="mt-6 inline-flex items-center gap-2 text-amber-400 font-medium hover:text-white"
              >
                Manufacturing capabilities
                <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="lg:col-span-7 grid grid-cols-2 gap-4">
              {[
                { t: "Special Purpose Motors", d: "BLDC, PMSM & custom precision" },
                { t: "Motor Controllers", d: "Field-oriented control & drives" },
                { t: "Precision Engineering", d: "Micron-grade tolerances" },
                { t: "Smart Devices", d: "IoT gateways & sensor systems" },
                { t: "Industrial Equipment", d: "Custom automation platforms" },
                { t: "Customized Products", d: "Engineered to your specs" },
              ].map((it) => (
                <div
                  key={it.t}
                  className="border border-white/10 bg-white/[0.03] rounded-lg p-5 hover:border-amber-400/50 transition-colors"
                >
                  <div className="font-display text-white font-semibold">
                    {it.t}
                  </div>
                  <div className="text-sm text-slate-300/80 mt-1">{it.d}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ---------- AI + AVIATION + SPACE + POWER (feature tiles) ---------- */}
      <section className="py-24 lg:py-28 border-b border-slate-200 reveal">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
            <div>
              <div className="text-xs uppercase tracking-[0.25em] text-amber-600 font-medium mb-3">
                Focus verticals
              </div>
              <h2 className="font-display text-3xl lg:text-5xl font-semibold text-[#0B1B3D] tracking-tight leading-[1.05]">
                AI, Aviation, Space & Power.
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {[
              {
                Icon: Bot,
                tag: "Artificial Intelligence",
                title: "Predictive, autonomous and intelligent systems.",
                items: [
                  "Predictive Maintenance",
                  "Smart Manufacturing",
                  "Machine Vision",
                  "Intelligent Control",
                  "Autonomous Systems",
                  "Industrial Analytics",
                ],
                image:
                  "https://images.unsplash.com/photo-1644325349124-d1756b79dd42?w=1200&q=80&auto=format&fit=crop",
              },
              {
                Icon: Plane,
                tag: "Aviation Technologies",
                title: "UAV, flight control & autonomous aviation.",
                items: [
                  "UAV Technologies",
                  "Drone Systems",
                  "Flight Control",
                  "Autonomous Aviation",
                  "Navigation Systems",
                ],
                image:
                  "https://images.unsplash.com/photo-1508614589041-895b88991e3e?w=1200&q=80&auto=format&fit=crop",
              },
              {
                Icon: Rocket,
                tag: "Space Sciences",
                title: "Instruments, subsystems & experimental platforms.",
                items: [
                  "Space Research",
                  "Satellite Technologies",
                  "Scientific Instrumentation",
                  "Space Electronics",
                  "Experimental Platforms",
                ],
                image:
                  "https://images.unsplash.com/photo-1773750453939-1d1d7219f1be?w=1200&q=80&auto=format&fit=crop",
              },
              {
                Icon: Zap,
                tag: "Power Sector",
                title: "Smart grid, storage & motor drive systems.",
                items: [
                  "Smart Grid",
                  "Renewable Energy",
                  "Solar Technologies",
                  "Motor Drives",
                  "Power Electronics",
                  "Energy Storage",
                ],
                image:
                  "https://images.unsplash.com/photo-1509391366360-2e959784a276?w=1200&q=80&auto=format&fit=crop",
              },
            ].map(({ Icon, tag, title, items, image }) => (
              <div
                key={tag}
                className="group relative border border-slate-200 rounded-2xl overflow-hidden bg-white hover:shadow-xl transition-shadow"
              >
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={image}
                    alt={tag}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0B1B3D]/70 to-transparent" />
                  <div className="absolute bottom-4 left-4 flex items-center gap-2.5">
                    <div className="w-9 h-9 rounded-md bg-white/95 flex items-center justify-center">
                      <Icon className="w-4 h-4 text-[#0B1B3D]" strokeWidth={1.5} />
                    </div>
                    <span className="text-[10px] uppercase tracking-[0.22em] text-amber-300 font-medium">
                      {tag}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="font-display text-xl lg:text-2xl font-semibold text-[#0B1B3D] leading-snug">
                    {title}
                  </h3>
                  <div className="flex flex-wrap gap-2 mt-4">
                    {items.map((it) => (
                      <span
                        key={it}
                        className="text-xs px-2.5 py-1 rounded-full border border-slate-200 text-slate-600"
                      >
                        {it}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- ASTRONOMY BAND ---------- */}
      <section className="py-24 lg:py-28 reveal">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-5">
              <div className="text-xs uppercase tracking-[0.25em] text-amber-600 font-medium mb-3 flex items-center gap-2">
                <Telescope className="w-4 h-4" strokeWidth={1.5} /> Astronomy
              </div>
              <h2 className="font-display text-3xl lg:text-4xl font-semibold text-[#0B1B3D] tracking-tight">
                From telescopes to public scientific outreach.
              </h2>
              <p className="mt-4 text-slate-600 leading-relaxed">
                We engineer observational instruments and support astrophotography,
                sky observation and scientific outreach programs for institutes and enthusiasts.
              </p>
            </div>
            <div className="lg:col-span-7 grid grid-cols-2 lg:grid-cols-3 gap-3">
              {[
                "Telescope Technologies",
                "Sky Observation",
                "Astrophotography",
                "Educational Astronomy",
                "Scientific Outreach",
                "Instrument Design",
              ].map((t) => (
                <div
                  key={t}
                  className="border border-slate-200 rounded-md p-4 text-sm text-slate-700 bg-white hover:border-sky-300 transition-colors"
                >
                  {t}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ---------- CTA ---------- */}
      <CTASection />
      <div className="py-24" />
    </div>
  );
};

export default Home;
